import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { NOTIFICATIONS_REFETCH_INTERVAL_MS } from 'components/notifications/notificationDisplay';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BaseService from 'services/api/BaseService';
import { parseResponseData } from 'services/api/httpResponse';
import { isRequestSuccessful } from 'services/api/httpStatus';
import { NotificationsUrls } from 'services/api/urls';
import {
  Notification,
  NotificationCategory,
  NotificationCategoryCounts,
  NotificationCounts,
  NotificationStatus,
  NotificationsResponse,
} from 'types/Notification';

const DEFAULT_LIMIT = 10;

const EMPTY_COUNTS: NotificationCounts = {
  unread: 0,
  read: 0,
  total: 0,
};

const EMPTY_CATEGORY_COUNTS: NotificationCategoryCounts = {
  all: 0,
  documents: 0,
  inProgress: 0,
  finalization: 0,
};

interface UseNotificationsOptions {
  status?: NotificationStatus;
  category?: NotificationCategory;
  limit?: number;
  enabled?: boolean;
}

export interface UseNotificationsResult {
  notifications: Notification[];
  counts: NotificationCounts;
  categoryCounts: NotificationCategoryCounts;
  hasMore: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  markAllAsRead: () => Promise<unknown>;
  markNotificationAsRead: (notificationId: string) => Promise<unknown>;
}

export const useNotifications = ({
  status = 'all',
  category = 'all',
  limit = DEFAULT_LIMIT,
  enabled = true,
}: UseNotificationsOptions = {}): UseNotificationsResult => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const notificationCountsRef = useRef<{
    counts?: NotificationCounts;
    categoryCountsByStatus: Partial<
      Record<NotificationStatus, NotificationCategoryCounts>
    >;
  }>({ categoryCountsByStatus: {} });

  const getErrorMessage = (data: unknown): string => {
    if (
      typeof data === 'object' &&
      data &&
      'message' in data &&
      typeof data.message === 'string'
    ) {
      return data.message;
    }

    return t('global:internet_connexion_error');
  };

  const notificationsQuery = useInfiniteQuery<NotificationsResponse, Error>({
    queryKey: ['notifications', status, category, limit],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      let data: unknown;

      try {
        const response = await BaseService.getRequest(
          NotificationsUrls.LIST({
            status,
            category,
            page: Number(pageParam),
            limit,
          }),
          true,
        );

        data = await parseResponseData(response);

        if (isRequestSuccessful(response.status)) {
          return data as NotificationsResponse;
        }
      } catch (error: unknown) {
        console.warn('Get notifications error', error);
        throw new Error(t('global:internet_connexion_error'));
      }

      throw new Error(getErrorMessage(data));
    },
    enabled,
    refetchInterval: NOTIFICATIONS_REFETCH_INTERVAL_MS,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.hasMore ? lastPage.pagination.page + 1 : undefined,
  });

  const invalidateNotifications = async () => {
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markAllAsReadMutation = useMutation<unknown, Error>({
    mutationFn: async () => {
      let data: unknown;

      try {
        const response = await BaseService.putRequest(
          NotificationsUrls.MARK_READ,
          {},
          true,
        );
        data = await parseResponseData(response);

        if (isRequestSuccessful(response.status)) {
          return data;
        }
      } catch (error: unknown) {
        console.warn('Mark all notifications read error', error);
        throw new Error(t('global:internet_connexion_error'));
      }

      throw new Error(getErrorMessage(data));
    },
    onSettled: invalidateNotifications,
  });

  /**
   * Optimistic UI: immediately removes the notification from all cached pages
   * so it disappears before the server responds. Returns a snapshot of the
   * previous cache so it can be restored on error.
   */
  const optimisticRemoveNotification = async (notificationId: string) => {
    await queryClient.cancelQueries({ queryKey: ['notifications'] });
    const previousData = queryClient.getQueriesData<
      InfiniteData<NotificationsResponse>
    >({ queryKey: ['notifications'] });
    queryClient.setQueriesData<InfiniteData<NotificationsResponse>>(
      { queryKey: ['notifications'] },
      (old) => {
        if (!old) return old!;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            notifications: page.notifications.filter(
              (n) => n.id !== notificationId,
            ),
          })),
        };
      },
    );
    return { previousData };
  };

  const markNotificationAsReadMutation = useMutation<
    unknown,
    Error,
    string,
    {
      previousData: [
        unknown,
        InfiniteData<NotificationsResponse> | undefined,
      ][];
    }
  >({
    mutationFn: async (notificationId) => {
      let data: { count: number } | undefined | string;

      try {
        const response = await BaseService.putRequest(
          NotificationsUrls.MARK_ONE_READ(notificationId),
          {},
          true,
        );
        data = await parseResponseData<{ count: number }>(response);

        if (
          isRequestSuccessful(response.status) &&
          data &&
          typeof data === 'object' &&
          'count' in data &&
          data.count > 0
        ) {
          return data;
        }
      } catch (error: unknown) {
        console.warn('Mark notification read error', error);
        throw new Error(t('global:internet_connexion_error'));
      }

      throw new Error(getErrorMessage(data));
    },
    onMutate: optimisticRemoveNotification,
    onError: (_err, _id, context) => {
      context?.previousData.forEach(([key, data]) => {
        queryClient.setQueryData(key as string[], data);
      });
    },
    onSettled: invalidateNotifications,
  });

  const notifications =
    notificationsQuery.data?.pages.flatMap((page) => page.notifications) ?? [];
  const latestCounts = notificationsQuery.data?.pages[0]?.counts;
  const latestCategoryCounts = notificationsQuery.data?.pages[0]?.categoryCounts;

  useEffect(() => {
    if (latestCounts) {
      notificationCountsRef.current.counts = latestCounts;
    }

    if (latestCategoryCounts) {
      notificationCountsRef.current.categoryCountsByStatus[status] =
        latestCategoryCounts;
    }
  }, [latestCounts, latestCategoryCounts, status]);

  const counts = latestCounts ?? notificationCountsRef.current.counts ?? EMPTY_COUNTS;
  const categoryCounts =
    latestCategoryCounts ??
    notificationCountsRef.current.categoryCountsByStatus[status] ??
    EMPTY_CATEGORY_COUNTS;

  return {
    notifications,
    counts,
    categoryCounts,
    hasMore: notificationsQuery.hasNextPage ?? false,
    isLoading: notificationsQuery.isLoading,
    isSuccess: notificationsQuery.isSuccess,
    isFetchingNextPage: notificationsQuery.isFetchingNextPage,
    fetchNextPage: async () => notificationsQuery.fetchNextPage(),
    markAllAsRead: async () => markAllAsReadMutation.mutateAsync(),
    markNotificationAsRead: markNotificationAsReadMutation.mutateAsync,
  };
};

export default useNotifications;
